import { Injectable } from "@angular/core";
import { FileInfo } from "../interfaces/FileInfo";
import { SupabaseClient, createClient } from "@supabase/supabase-js";
import { environment } from "src/environments/environment";
import { Ng2ImgMaxService } from "ng2-img-max";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { blobToBase64 } from "src/app/utils/BlobToBase64";
import { FileOpener } from "@capacitor-community/file-opener";
import { isPlatform } from "@ionic/angular";
import { AuthStorage } from "src/app/core/storage/auth-storage.class";
import { SupabaseClientService } from "./supabase-client";

@Injectable({
  providedIn: "root",
})
export class FileService {
  private supabase: SupabaseClient;

  constructor(
    private ng2ImgMax: Ng2ImgMaxService,
    private supabaseClientService: SupabaseClientService
  ) {
    this.supabase = supabaseClientService.supabaseClient;
  }

  async uploadCameraFile(fileDb: string, cameraFile: any, info: FileInfo) {
    let file = null;

    const blob = await fetch(cameraFile.webPath).then((r) => r.blob());
    file = new File([blob], cameraFile.path, { type: blob.type });

    try {
      this.ng2ImgMax.compressImage(file, 0.05).subscribe(
        async (compressedFile) => {
          const { data, error } = await this.supabase.storage
            .from(fileDb)
            .upload(info.path, compressedFile, {
              cacheControl: "60",
              upsert: true,
            });
        },
        async (error) => {
          console.error("Compression error: ", error);

          // Use the original file if compression fails
          const { data, error: uploadError } = await this.supabase.storage
            .from(fileDb)
            .upload(info.path, file, {
              cacheControl: "60",
              upsert: true,
            });
        }
      );
    } catch (err) {
      console.error("Unexpected error: ", err);
      // Use the original file in case of unexpected errors
      const { data, error: uploadError } = await this.supabase.storage
        .from(fileDb)
        .upload(info.path, file, {
          cacheControl: "60",
          upsert: true,
        });
      throw err;
    }

    return blob;
  }

  async uploadFile(fileDb: string, pdf: any, info: FileInfo) {
    let file = null;

    try {
      const blob = await fetch(pdf).then((r) => r.blob());
      file = new File([blob], info.path, { type: blob.type });

      this.ng2ImgMax.compressImage(file, 0.05).subscribe(
        async (compressedFile) => {
          const { data, error } = await this.supabase.storage
            .from(fileDb)
            .upload(info.path, compressedFile, {
              cacheControl: "6200",
              upsert: true,
            });

          if (error) {
            throw error;
          }
        },
        async (error) => {
          console.error("Compression error: ", error);

          const { data, error: uploadError } = await this.supabase.storage
            .from(fileDb)
            .upload(info.path, file, {
              cacheControl: "6200",
              upsert: true,
            });
        }
      );
    } catch (err) {
      throw err;
    }
  }

  async downloadFile(fileDb: string, info: FileInfo) {
    let file = null;

    try {
      const { data, error } = await this.supabase.storage
        .from(fileDb)
        .download(info.path);

      if (error) {
        throw error;
      }

      file = data;
    } catch (err) {
      throw err;
    }

    return file;
  }

  async deleteFile(fileDb: string, fileName: string) {
    try {
      const { data, error } = await this.supabase.storage
        .from(fileDb)
        .remove([fileName]);

      if (error) {
        throw error;
      }
    } catch (err) {
      throw err;
    }
  }

  async openFile(fileName: string, blob: Blob, type?: string) {
    let fileUrl = null;

    try {
      if (
        type === "application/pdf" ||
        blob.type === "application/x-www-form-urlencoded"
      ) {
        blob = new Blob([blob], { type: "application/pdf" });
      }

      // convert blob to base64
      const base64 = (await blobToBase64(blob)) as string;

      // Save the blob to the filesystem
      Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Data,
        recursive: true,
      }).then((uriResult) => {
        return FileOpener.open({
          filePath: uriResult?.uri,
          contentType: blob.type,
        });
      });

      // also open the file in a new window (tab) with window.open
      if (!isPlatform("capacitor")) {
        fileUrl = URL.createObjectURL(blob);

        window.open(fileUrl);
      }
    } catch (err) {
      throw err;
    }
  }

  async deleteUserBuckets(userId: string) {
    const { data:list } = await this.supabase.storage.from("logos").list(`${userId}`);
    const filesToRemove = list.map((x) => `${userId}/${x.name}`);
    if (filesToRemove.length > 0) {
      await this.supabase.storage.from("logos").remove(filesToRemove);
    }
    
    const { data:list2 } = await this.supabase.storage.from("profile_pictures").list(`${userId}`);
    const filesToRemove2 = list2.map((x) => `${userId}/${x.name}`);
    if (filesToRemove2.length > 0) {
      await this.supabase.storage.from("profile_pictures").remove(filesToRemove2);
    }

    const { data:list3 } = await this.supabase.storage.from("reports").list(`${userId}`);
    const filesToRemove3 = list3.map((x) => `${userId}/${x.name}`);
    if (filesToRemove3.length > 0) {
      await this.supabase.storage.from("reports").remove(filesToRemove3);
    }

    const { data:list4 } = await this.supabase.storage.from("invoices").list(`${userId}`);
    const filesToRemove4 = list4.map((x) => `${userId}/${x.name}`);
    if (filesToRemove4.length > 0) {
      await this.supabase.storage.from("invoices").remove(filesToRemove4);
    }

    const { data:list5 } = await this.supabase.storage.from("attachments").list(`${userId}`);
    const filesToRemove5 = list5.map((x) => `${userId}/${x.name}`);
    if (filesToRemove5.length > 0) {
      await this.supabase.storage.from("attachments").remove(filesToRemove5);
    }
  }
}
